import { useCallback } from 'react';
import sortBy from 'lodash/sortBy';

import { DIRECTION_LEFT } from 'webclient.constants';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  Sortable,
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { Box } from 'approot/shared/design-system/box/box.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import customNanoId from 'lib/nanoid/nanoid';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import Switch from 'approot/shared/switch/switch';
import { EditProps, RiveAnimationProperties, TextRun } from '../rive.constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { POSITION_BOTTOM, POSITION_RIGHT } from 'global/constants';

const RiveSettingsButtons = ({
  properties,
  onUpdate,
}: EditProps & {
  onUpdate: (properties: RiveAnimationProperties) => void;
}) => {
  const sortedItems = sortBy(properties.rive?.buttons || [], 'sort');

  const update = useCallback(
    (buttons: TextRun[]) => {
      onUpdate({
        ...properties,
        rive: {
          ...properties.rive,
          buttons,
        },
      });
    },
    [properties, onUpdate]
  );

  const handleAdd = useCallback(
    (sortedFields: TextRun[]) => {
      update([
        ...sortedFields,
        {
          __typename: 'RiveTextRun',
          itemId: customNanoId(),
          text: 'Label',
          sort: getReorderSort(
            sortedFields,
            sortedFields.length,
            DIRECTION_LEFT
          ),
        },
      ]);
    },
    [update]
  );

  const handleUpdate = useCallback(
    (item: TextRun, sortedFields: TextRun[]) => {
      update(sortedFields.map(s => (s.itemId === item.itemId ? item : s)));
    },
    [update]
  );

  const handleDelete = useCallback(
    (itemId: string, sortedFields: TextRun[]) => {
      update(sortedFields.filter(s => s.itemId !== itemId));
    },
    [update]
  );

  return (
    <>
      <SettingsGroup>
        <SettingsLabel>Enable Buttons</SettingsLabel>
        <Switch
          ariaLabel="Enable Buttons"
          state={!!properties.rive?.enableButtons}
          onClick={() =>
            onUpdate({
              ...properties,
              rive: {
                ...properties.rive,
                enableButtons: !properties.rive?.enableButtons,
              },
            })
          }
        />
      </SettingsGroup>

      {properties.rive?.enableButtons && (
        <>
          <SettingsGroup>
            <SettingsLabel>Layout</SettingsLabel>
            <CheckableSingle
              name="buttons-position"
              selected={properties.rive?.buttonsPosition || POSITION_BOTTOM}
              options={[
                { label: 'Bottom', value: POSITION_BOTTOM },
                { label: 'Right', value: POSITION_RIGHT },
              ]}
              onSelect={value => {
                onUpdate({
                  ...properties,
                  rive: {
                    ...properties.rive,
                    buttonsPosition: value,
                  },
                });
              }}
            />
          </SettingsGroup>

          <Sortable<TextRun>
            key={sortedItems.map(s => s.itemId).join(',')}
            list={sortedItems}
            renderItem={(item, index) => {
              const dataItem = properties?.rive?.buttons?.find(
                s => s.itemId === item.itemId
              );
              return (
                <SortableItem key={item.itemId}>
                  <SortableHeader>
                    <DeleteButtonIcon
                      $isSmall
                      aria-label={`Delete option ${index + 1}`}
                      data-selector="icon-delete"
                      onClick={() => handleDelete(item.itemId, sortedItems)}
                    />
                  </SortableHeader>
                  <SortableContent>
                    <PStyled marginBottom={4}>Button {index + 1}</PStyled>
                    <AutoResizingTextArea
                      data-selector="rive-btn-text-input"
                      showCounter
                      maxLength={25}
                      id={`field-${index}`}
                      name={`field-${index}`}
                      initialValue={dataItem?.text}
                      onChange={e =>
                        handleUpdate(
                          { ...item, text: e.target.value },
                          sortedItems
                        )
                      }
                    />
                  </SortableContent>
                </SortableItem>
              );
            }}
            onSortChange={({ sourceIndex, destinationIndex, direction }) => {
              const field = sortedItems[sourceIndex];
              if (sourceIndex !== destinationIndex) {
                const newSort = getReorderSort<TextRun>(
                  sortedItems,
                  destinationIndex,
                  direction
                );
                handleUpdate({ ...field, sort: newSort }, sortedItems);
              }
            }}
          />
          <Box $flexDirection="column" $marginBottom={24}>
            <SecondaryButton
              type="button"
              disabled={sortedItems.length >= 4}
              onClick={() => handleAdd(sortedItems)}
              data-selector="btn-add"
            >
              Add Button
            </SecondaryButton>
          </Box>
        </>
      )}
    </>
  );
};

export default RiveSettingsButtons;
